import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy loading components
const Home = () => import('@/views/Home.vue')
const Login = () => import('@/views/auth/Login.vue')
const Register = () => import('@/views/auth/Register.vue')
const ForgotPassword = () => import('@/views/auth/ForgotPassword.vue')

// Public pages
const Courses = () => import('@/views/public/Courses.vue')
const CoursesTests = () => import('@/views/public/CoursesTests.vue')
const PublicInstructors = () => import('@/views/public/InstructorsPublic.vue')
const PublicTests = () => import('@/views/public/TestsPublic.vue')
const About = () => import('@/views/public/About.vue')
const Contact = () => import('@/views/public/Contact.vue')
const Privacy = () => import('@/views/public/Privacy.vue')
const Terms = () => import('@/views/public/Terms.vue')
const Cookies = () => import('@/views/public/Cookies.vue')

// Student routes
const StudentDashboard = () => import('@/views/student/Dashboard.vue')
const LimitedStudentDashboard = () => import('@/views/student/LimitedStudentDashboard.vue')
const TestSystem = () => import('@/views/student/TestSystem.vue')
const Profile = () => import('@/views/student/Profile.vue')
const Instructors = () => import('@/views/student/Instructors.vue')
const Schedule = () => import('@/views/student/Schedule.vue')

// Instructor routes
const InstructorDashboard = () => import('@/views/instructor/Dashboard.vue')
const Students = () => import('@/views/instructor/Students.vue')
const InstructorSchedule = () => import('@/views/instructor/Schedule.vue')
const InstructorAllSchedules = () => import('@/views/admin/InstructorSchedules.vue')
const InstructorProfile = () => import('@/views/instructor/Profile.vue')

// Theory Teacher routes
const TeacherDashboard = () => import('@/views/teacher/Dashboard.vue')
const TeacherStudents = () => import('@/views/teacher/Students.vue')
const TestManagement = () => import('@/views/teacher/TestManagement.vue')

// Admin routes
const AdminDashboard = () => import('@/views/admin/Dashboard.vue')
const AdminProfile = () => import('@/views/admin/Profile.vue')
const UserManagement = () => import('@/views/admin/UserManagement.vue')
const InstructorManagement = () => import('@/views/admin/InstructorManagement.vue')
const AdminTestManagement = () => import('@/views/admin/TestManagement.vue')
const LessonManagement = () => import('@/views/admin/LessonManagement.vue')
const InstructorSchedules = () => import('@/views/admin/InstructorSchedules.vue')
const Settings = () => import('@/views/admin/Settings.vue')
const SettingsManagement = () => import('@/views/admin/SettingsManagement.vue')
const ServerStatus = () => import('@/views/admin/ServerStatus.vue')
const ErrorLogs = () => import('@/views/admin/ErrorLogs.vue')
const BotSendMessage = () => import('@/views/admin/BotSendMessage.vue')
const AiSettings = () => import('@/views/admin/AiSettings.vue')

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false, hideForAuth: true },
    alias: ['/auth/login']
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { requiresAuth: false, hideForAuth: true },
    alias: ['/auth/register']
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false, hideForAuth: true },
    alias: ['/auth/forgot-password']
  },

  // Public informational routes (for Navbar)
  { path: '/courses', name: 'Courses', component: Courses, meta: { requiresAuth: false } },
  { path: '/courses-tests', name: 'CoursesTests', component: CoursesTests, meta: { requiresAuth: false } },
  { path: '/instructors', name: 'PublicInstructors', component: PublicInstructors, meta: { requiresAuth: false } },
  { path: '/tests', name: 'PublicTests', component: PublicTests, meta: { requiresAuth: false } },
  { path: '/about', name: 'About', component: About, meta: { requiresAuth: false } },
  { path: '/contact', name: 'Contact', component: Contact, meta: { requiresAuth: false } },
  { path: '/privacy', name: 'Privacy', component: Privacy, meta: { requiresAuth: false } },
  { path: '/terms', name: 'Terms', component: Terms, meta: { requiresAuth: false } },
  { path: '/cookies', name: 'Cookies', component: Cookies, meta: { requiresAuth: false } },

  // Generic routes that redirect based on role
  {
    path: '/dashboard',
    name: 'Dashboard',
    redirect: (to) => {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return '/login'
      const roleMap = {
        'STUDENT': '/student',
        'INSTRUCTOR': '/instructor',
        'THEORY_TEACHER': '/teacher',
        'ADMIN': '/admin'
      }
      return roleMap[authStore.user?.role] || '/student'
    }
  },
  {
    path: '/profile',
    redirect: (to) => {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return '/login'
      const roleMap = {
        'STUDENT': '/student/profile',
        'INSTRUCTOR': '/instructor/profile',
        'THEORY_TEACHER': '/teacher',
        'ADMIN': '/admin/profile'
      }
      return roleMap[authStore.user?.role] || '/student/profile'
    }
  },
  {
    path: '/settings',
    redirect: (to) => {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return '/login'
      const roleMap = {
        'STUDENT': '/student/profile',
        'INSTRUCTOR': '/instructor/profile',
        'THEORY_TEACHER': '/teacher',
        'ADMIN': '/admin/settings'
      }
      return roleMap[authStore.user?.role] || '/admin/settings'
    }
  },
  
  // Student routes
  {
    path: '/student',
    children: [
      {
        path: '',
        name: 'StudentDashboard',
        component: StudentDashboard,
        meta: { requiresAuth: true, roles: ['STUDENT'] }
      },
      {
        path: 'dashboard',
        name: 'StudentDashboardAlias',
        component: StudentDashboard,
        meta: { requiresAuth: true, roles: ['STUDENT'] }
      },
    ]
  },
  
  // Limited Student routes
  {
    path: '/limited-student',
    children: [
      {
        path: '',
        name: 'LimitedStudentDashboard',
        component: LimitedStudentDashboard,
        meta: { requiresAuth: true, roles: ['LIMITED_STUDENT'] }
      },
      {
        path: 'dashboard',
        name: 'LimitedStudentDashboardAlias',
        component: LimitedStudentDashboard,
        meta: { requiresAuth: true, roles: ['LIMITED_STUDENT'] }
      }
    ]
  },

  {
    path: '/student',
    children: [
      {
        path: 'tests',
        name: 'TestSystem',
        component: TestSystem,
        meta: { requiresAuth: true, roles: ['STUDENT'], restrictedRoles: ['LIMITED_STUDENT'] }
      },
      {
        path: 'instructors',
        name: 'Instructors',
        component: Instructors,
        meta: { requiresAuth: true, roles: ['STUDENT'] }
      },
      {
        path: 'schedule',
        name: 'Schedule',
        component: Schedule,
        meta: { requiresAuth: true, roles: ['STUDENT'] }
      },
      {
        path: 'profile',
        name: 'Profile',
        component: Profile,
        meta: { requiresAuth: true, roles: ['STUDENT'] }
      }
    ]
  },

  // Instructor routes
  {
    path: '/instructor',
    children: [
      {
        path: '',
        redirect: { name: 'InstructorDashboard' }
      },
      {
        path: 'dashboard',
        name: 'InstructorDashboard',
        component: InstructorDashboard,
        meta: { requiresAuth: true, roles: ['INSTRUCTOR'] }
      },
      {
        path: 'students',
        name: 'Students',
        component: Students,
        meta: { requiresAuth: true, roles: ['INSTRUCTOR'] }
      },
      {
        path: 'schedule',
        name: 'InstructorSchedule',
        component: InstructorSchedule,
        meta: { requiresAuth: true, roles: ['INSTRUCTOR'] }
      },
      {
        path: 'all-schedules',
        name: 'InstructorAllSchedules',
        component: InstructorAllSchedules,
        meta: { requiresAuth: true, roles: ['INSTRUCTOR'], requiresAllScheduleView: true }
      },
      {
        path: 'profile',
        name: 'InstructorProfile',
        component: InstructorProfile,
        meta: { requiresAuth: true, roles: ['INSTRUCTOR'] }
      }
    ]
  },

  // Theory Teacher routes
  {
    path: '/teacher',
    children: [
      {
        path: '',
        name: 'TeacherDashboard',
        component: TeacherDashboard,
        meta: { requiresAuth: true, roles: ['THEORY_TEACHER'] }
      },
      {
        path: 'dashboard',
        name: 'TeacherDashboardAlias',
        component: TeacherDashboard,
        meta: { requiresAuth: true, roles: ['THEORY_TEACHER'] }
      },
      {
        path: 'students',
        name: 'TeacherStudents',
        component: TeacherStudents,
        meta: { requiresAuth: true, roles: ['THEORY_TEACHER'] }
      },
      {
        path: 'tests',
        name: 'TestManagement',
        component: TestManagement,
        meta: { requiresAuth: true, roles: ['THEORY_TEACHER'] }
      }
    ]
  },

  // Admin routes
  {
    path: '/admin',
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: AdminDashboard,
        meta: { requiresAuth: true, roles: ['ADMIN', 'ADMIN_LIMITED'] }
      },
      {
        path: 'dashboard',
        name: 'AdminDashboardAlias',
        component: AdminDashboard,
        meta: { requiresAuth: true, roles: ['ADMIN', 'ADMIN_LIMITED'] }
      },
      {
        path: 'profile',
        name: 'AdminProfile',
        component: AdminProfile,
        meta: { requiresAuth: true, roles: ['ADMIN', 'ADMIN_LIMITED'] }
      },
      {
        path: 'users',
        name: 'UserManagement',
        component: UserManagement,
        meta: { requiresAuth: true, roles: ['ADMIN', 'ADMIN_LIMITED'] }
      },
      {
        path: 'instructors',
        name: 'InstructorManagement',
        component: InstructorManagement,
        meta: { requiresAuth: true, roles: ['ADMIN', 'ADMIN_LIMITED'] }
      },
      {
        path: 'tests',
        name: 'AdminTestManagement',
        component: AdminTestManagement,
        meta: { requiresAuth: true, roles: ['ADMIN'] } // Тест редакциясы тек ADMIN үшін
      },
      {
        path: 'lessons',
        name: 'LessonManagement',
        component: LessonManagement,
        meta: { requiresAuth: true, roles: ['ADMIN'] }
      },
      {
        path: 'schedule',
        name: 'InstructorSchedules',
        component: InstructorSchedules,
        meta: { requiresAuth: true, roles: ['ADMIN', 'ADMIN_LIMITED'] }
      },
      {
        path: 'test-system',
        name: 'AdminTestSystem',
        component: TestSystem,
        meta: { requiresAuth: true, roles: ['ADMIN', 'ADMIN_LIMITED'] }
      },
      {
        path: 'settings',
        name: 'Settings',
        component: Settings,
        meta: { requiresAuth: true, roles: ['ADMIN'] }
      },
      {
        path: 'settings-management',
        name: 'SettingsManagement',
        component: SettingsManagement,
        meta: { requiresAuth: true, roles: ['ADMIN'] }
      },
      {
        path: 'actions',
        name: 'AdminActions',
        component: () => import('@/views/admin/AdminActions.vue'),
        meta: { requiresAuth: true, roles: ['ADMIN'] }
      },
      {
        path: 'mail',
        name: 'AdminMail',
        component: () => import('@/views/admin/Mail.vue'),
        meta: { requiresAuth: true, roles: ['ADMIN'] }
      },
      {
        path: 'server-status',
        name: 'ServerStatus',
        component: ServerStatus,
        meta: { requiresAuth: true, roles: ['ADMIN'] }
      },
      {
        path: 'error-logs',
        name: 'ErrorLogs',
        component: ErrorLogs,
        meta: { requiresAuth: true, roles: ['ADMIN'] }
      },
      {
        path: 'bot-send-message',
        alias: '/admin/telegram',
        name: 'BotSendMessage',
        component: BotSendMessage,
        meta: { requiresAuth: true, roles: ['ADMIN'] }
      },
      {
        path: 'student-analytics',
        name: 'StudentAnalytics',
        component: () => import('@/views/admin/StudentAnalytics.vue'),
        meta: { requiresAuth: true, roles: ['ADMIN', 'ADMIN_LIMITED'] }
      },
      {
        path: 'ai-settings',
        name: 'AiSettings',
        component: AiSettings,
        meta: { requiresAuth: true, roles: ['ADMIN'] }
      }
    ]
  },

  // Catch all 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Wait for auth initialization if not yet initialized
  if (!authStore.initialized) {
    let attempts = 0
    while (!authStore.initialized && attempts < 20) {
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }
  }
  
  // If we have a token but no user and not currently loading, verify token
  if (authStore.token && !authStore.user && !authStore.loading && !authStore.verifyingToken) {
    const verified = await authStore.verifyToken()
  }
  
  // Save the last route for authenticated users (except login/register pages)
  if (authStore.isAuthenticated && 
      !['Login', 'Register', 'ForgotPassword'].includes(to.name) &&
      from.name !== null) {  // not the initial navigation
    authStore.setLastRoute(to.path)
  }
  
  // Hide auth pages for already authenticated users
  if (to.meta.hideForAuth && authStore.isAuthenticated) {
    const redirectTo = authStore.getRedirectRoute()
    next(redirectTo)
    return
  }
  
  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Save intended route for redirect after login
      // Console log removed
      next(`/login?redirect=${to.path}`)
      return
    }
    
    // Check role permissions
    if (to.meta.roles && !to.meta.roles.includes(authStore.user?.role)) {
      // Special handling for LIMITED_STUDENT trying to access restricted features
      if (to.meta.restrictedRoles && to.meta.restrictedRoles.includes(authStore.user?.role)) {
        // Store the intended route to show in the restriction message
        localStorage.setItem('korkem-auto-restrictedRoute', to.path)
        localStorage.setItem('korkem-auto-restrictedFeature', to.name || 'TestSystem')
        next('/student?restricted=true')
        return
      }
      
      // Redirect to appropriate dashboard based on role
      next(authStore.getDefaultRouteForRole(authStore.user?.role))
      return
    }

    if (to.meta.requiresAllScheduleView && authStore.user?.canViewAllSchedules !== true) {
      next(authStore.getDefaultRouteForRole(authStore.user?.role))
      return
    }
  }
  
  next()
})

// Handle dynamic import failures (stale cache after deploy)
router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module')
  ) {
    // Prevent infinite reload loop
    const lastReload = sessionStorage.getItem('korkem-auto-chunk-reload')
    const now = Date.now()
    if (!lastReload || now - Number(lastReload) > 10000) {
      sessionStorage.setItem('korkem-auto-chunk-reload', String(now))
      window.location.assign(to?.fullPath || window.location.href)
    }
  }
})

export default router
